/**
 * Locale-aware date / number / size formatters.
 */

const RELATIVE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export function formatRelative(
  iso: string,
  locale: string = navigator.language ?? "ja-JP",
): string {
  const date = new Date(iso);
  const now = Date.now();
  const diff = now - date.getTime();
  if (Number.isNaN(date.getTime())) return iso;

  if (Math.abs(diff) < RELATIVE_THRESHOLD_MS) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const minutes = Math.round(diff / 60_000);
    if (Math.abs(minutes) < 1) return rtf.format(0, "minute");
    if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
    const hours = Math.round(minutes / 60);
    return rtf.format(-hours, "hour");
  }
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 ? 0 : 1)} ${units[i]}`;
}

/** ¥1,234,567 form (no $ or M shortcuts — JP-first per CLAUDE.md). */
export function formatYen(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(value);
}
